import "../tiptap-toolbar.css";

import BorderColorIcon from "@mui/icons-material/BorderColor";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Popover, Tooltip } from "@mui/material";
import { useEffect, useState } from "react";

import { 
	getHighlightColorNames,
	getHighlightColors,
	getTiptapColorNames, 
	getTiptapColors, 
	HIGHLIGHT_COLORS,
	HIGHLIGHT_ICON_COLORS,
	TIPTAP_COLORS} from "../colorConstants";
import MenuButton from "../MenuButton";

const UnifiedColorPicker = ({ 
	editor, 
	type = "text", // "text" or "highlight"
	iconOnly = false // true for highlight, false for text color
}) => {
	const [anchorEl, setAnchorEl] = useState(null);

	const colorPalette = type === "highlight" 
		? getHighlightColors()
		: (type === "text" ? getTiptapColors() : []);

	const colorNameMap = type === "highlight" 
		? getHighlightColorNames()
		: getTiptapColorNames();
	
	const defaultColor = type === "highlight" 
		? HIGHLIGHT_COLORS.YELLOW 
		: TIPTAP_COLORS.BLACK;
	
	const [selectedColor, setSelectedColor] = useState(defaultColor);

	useEffect(() => {
		if (!editor) return;
		
		const updateSelectedColor = () => {
			if (type === "text") {
				const currentColor = editor.getAttributes("textStyle").color;
				if (currentColor) {
					setSelectedColor(currentColor);
				} else {
					setSelectedColor(defaultColor);
				}
			} else if (type === "highlight") {
				if (editor.isActive("highlight")) {
					const attrs = editor.getAttributes("highlight");
					if (attrs.color && colorPalette.includes(attrs.color)) {
						setSelectedColor(attrs.color);
					}
				}
			}
		};
		
		editor.on("selectionUpdate", updateSelectedColor);
		editor.on("update", updateSelectedColor);
		editor.on("focus", updateSelectedColor);
		
		updateSelectedColor();
		
		return () => {
			editor.off("selectionUpdate", updateSelectedColor);
			editor.off("update", updateSelectedColor);
			editor.off("focus", updateSelectedColor);
		};
	}, [editor, type, colorPalette, defaultColor]);

	const handleOpen = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const applyColor = (color) => {
		if (!editor) return;
		
		if (type === "text") {
			editor.chain().focus().setColor(color).run();
		} else if (type === "highlight") {
			if (editor.isActive("highlight", { color })) {
				editor.chain().focus().unsetHighlight().run();
			} else {
				editor.chain().focus().setHighlight({ color }).run();
			}
		}
		
		setSelectedColor(color);
		handleClose();
	};

	if (!editor) return null;

	const isActive = type === "highlight" ? editor.isActive("highlight") : true;
	const iconColor = type === "highlight" && HIGHLIGHT_ICON_COLORS[selectedColor] 
		? HIGHLIGHT_ICON_COLORS[selectedColor] 
		: HIGHLIGHT_ICON_COLORS[HIGHLIGHT_COLORS.YELLOW];
	
	return (
		<>
			<Tooltip title={type === "text" ? "Text Color" : "Highlight"}>
				<Box sx={{ 
					display: "flex", 
					alignItems: "center",
					mr: 1
				}}>
					<MenuButton 
						isActive={isActive}
						onClick={handleOpen}
						sx={{ display: "flex", alignItems: "center", pr: 0.5 }}
					>
						{iconOnly ? (
							<BorderColorIcon 
								fontSize="small" 
								sx={{
									color: iconColor,
									filter: isActive ?
										"drop-shadow(0px 0px 2px rgba(0, 0, 0, 0.2))" :
										"none",
									transition: "filter 0.2s ease" 
								}} 
							/>
						) : (
							<Box 
								sx={{ 
									width: 20,
									height: 20,
									backgroundColor: selectedColor,
									border: "1px solid #ddd",
									borderRadius: "4px",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
								aria-label={`Current ${type} color: ${colorNameMap[selectedColor] || "Custom"}`}
							/>
						)}
						<ExpandMoreIcon fontSize="small" sx={{ ml: 0.5 }} />
					</MenuButton>
				</Box>
			</Tooltip>
			
			<Popover
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left",
				}}
				slotProps={{
					paper: {
						sx: { 
							padding: "8px",
							overflow: "hidden"
						}
					}
				}}
			>
				<Box sx={{ 
					display: "flex", 
					flexWrap: "wrap",
					width: "84px", // 3 boxes x (20px + 8px margin)
					m: "-4px", // Compensate for child margins
				}}>
					{colorPalette.map(color => (
						<Box 
							key={color}
							sx={{ 
								width: 20,
								height: 20,
								backgroundColor: color,
								cursor: "pointer",
								border: `1px solid ${color === selectedColor && isActive ? "#666" : "#ddd"}`,
								borderRadius: "4px",
								m: "4px",
								"&:hover": {
									boxShadow: "0 0 0 2px rgba(0,0,0,0.1)"
								},
								"&:focus": {
									outline: "2px solid #1976d2",
									outlineOffset: "2px"
								}
							}} 
							onClick={() => applyColor(color)}
							onKeyDown={(e) => {
								if (e.key === "Enter" || e.key === " ") {
									e.preventDefault();
									applyColor(color);
								}
							}}
							role="button"
							tabIndex={0}
							aria-label={`${type === "text" ? "Set text color to" : "Highlight text with"} ${colorNameMap[color] || "Custom"}`}
						/>
					))}
				</Box>
			</Popover>
		</>
	);
};

export default UnifiedColorPicker;